import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  MenuItem,
  Typography,
  ButtonBase,
  Chip,
} from "@mui/material";
import {
  ArrowDropUp,
  ArrowDropDown,
  PlayArrow,
  UnfoldMore,
  BorderColor,
  InfoOutlined,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import OrderEntry from "./OrderEntry";
import { applyRenderFunctions } from "./OrderComponents/columnRendererRegistry";
import { clearConfigIfOutdated } from "../Utils/columnVersionManager";
import { getPreference, setPreference } from "../Utils/userPreferenceManger";
import { HIGH_PRECISION_NUMERIC, NUMERIC_COLUMNS } from '../Constants/globalColConfig';
import { tokens, withAlpha } from "../Common/designTokens"; // added
import { utcToLocal } from "../Utils/timeConversions";
import { ENHANCED_RFQ_PROTOCOLS, PROTOCOL_COLUMN_WIDTH } from "./OrderComponents/RfqComponents/RfqConstants";
import RfqBranchTable from "./OrderComponents/RfqBranchTable";
import MABranchTable from "./MAInventoryComponents/MABranchTable";
import { useAuth } from "../Context/AuthProvider";
import { isStagingOrder } from "../Constants/stagingBlotter";
import { getListRfqGroupKey, groupListRfqOrders } from "../Utils/groupListRfqOrders";
import { formatListId } from "../Utils/listOrderId";
import { hasCancellableListItem, isListNameEditEligible } from "../Utils/listRfqStatus";
import { useOpenListView } from "./OrderComponents/columnConfig";

// Ensure these remain the single source of truth for fixed widths
const PROTOCOL_WIDTH = PROTOCOL_COLUMN_WIDTH;
const VIEW_INVENTORY_WIDTH = 280;
const ACTION_MIN_WIDTH = 170;
const MA_ACTION_MIN_WIDTH = 108;
const MA_SOURCE_WIDTH = 80;
// Update FIXED_COLUMNS to include viewInventory before account
const FIXED_COLUMNS = ["viewInventory", "actions", "protocol", "account", "action_account_pos"];
// Columns that should not display sort icons
const UNSORTABLE_COLUMNS = ["viewInventory", "actions", "action_account_pos"];

const isActionColumnKey = (columnKey = "") =>
  columnKey === "actions" ||
  columnKey === "action_account_pos" ||
  String(columnKey).includes("action");

const getFixedColumnsForSection = (sectionKey) => {
  if (sectionKey === "inventory") {
    return ["action"];
  }
  if (sectionKey === "ediFileProcess") {
    return ["actionEdiFileProcess"];
  }
  if (sectionKey === "ma-inventory") {
    // Keep core MA Inventory columns locked in place.
    return [
      "actions",
      "source",
      "cusip",
    ];
  }
  if (sectionKey === "sec-master") {
    return ["identifier"];
  }
  return FIXED_COLUMNS;
};

const getGroupedColumnsForSection = (sectionKey) => {
  if (sectionKey === "ma-inventory") {
    return [
      "bidDealer",
      "bidYield",
      "bidQuantity",
      "bidPrice",
      "offerPrice",
      "offerQuantity",
      "offerYield",
      "offerDealer",
    ];
  }
  return [];
};

const DEFAULT_DYNAMIC_COLUMN_WIDTH = 150;
const INVENTORY_DYNAMIC_COLUMN_WIDTH = 85;

const MA_INVENTORY_DEFAULT_WIDTHS = {
  cusip: 140,
  bidDealer: 92,
  bidYield: 96,
  bidQuantity: 96,
  bidPrice: 89,
  offerQuantity: 109,
  offerPrice: 103,
  offerYield: 96,
  offerDealer: 95,
  type: 81,
  securityCharge: 90,
  description: 304,
  subType: 103,
};

const getDefaultWidthForColumn = (column, source) => {
  if (!column) return source === "inventory" ? INVENTORY_DYNAMIC_COLUMN_WIDTH : DEFAULT_DYNAMIC_COLUMN_WIDTH;
  if (column.key === "protocol") return PROTOCOL_WIDTH;
  if (column.key === "viewInventory") return VIEW_INVENTORY_WIDTH;
  if (column.key === "actions") return source === "ma-inventory" ? MA_ACTION_MIN_WIDTH : ACTION_MIN_WIDTH;
  if (column.key === "source" && source === "ma-inventory") return MA_SOURCE_WIDTH;
  if (source === "ma-inventory" && MA_INVENTORY_DEFAULT_WIDTHS[column.key] != null) return MA_INVENTORY_DEFAULT_WIDTHS[column.key];
  if (column.width != null) return column.width;
  return source === "inventory" ? INVENTORY_DYNAMIC_COLUMN_WIDTH : DEFAULT_DYNAMIC_COLUMN_WIDTH;
};

const getActionMinWidthForSection = (sectionKey, source) => {
  if (sectionKey === "ma-inventory" || source === "ma-inventory") {
    return MA_ACTION_MIN_WIDTH;
  }
  return ACTION_MIN_WIDTH;
};

const getInitialColumnWidths = (selectedColumns = [], sectionKey, source) => {
  const savedWidths = getPreference(`columnWidths_${sectionKey}`) || {};
  const actionMinWidth = getActionMinWidthForSection(sectionKey, source);
  const initial = {
    ...savedWidths,
    protocol: PROTOCOL_WIDTH,
    viewInventory: VIEW_INVENTORY_WIDTH,
    actions: actionMinWidth,
    source: source === "ma-inventory" ? MA_SOURCE_WIDTH : savedWidths?.source,
  };

  selectedColumns.forEach((col) => {
    if (initial[col.key] != null) return;
    initial[col.key] = getDefaultWidthForColumn(col, source);
  });

  return initial;
};

const serializeColumnKeys = (columns = []) =>
  columns.map((col) => col?.key).filter(Boolean);

const extractColumnKeys = (savedOrder) => {
  if (!Array.isArray(savedOrder)) return [];
  return savedOrder
    .map((item) => (typeof item === "string" ? item : item?.key))
    .filter(Boolean);
};

const mergeColumnsWithSavedOrder = (columns = [], savedOrder) => {
  const savedKeys = extractColumnKeys(savedOrder);
  if (!columns.length || !savedKeys.length) return columns;
  const remaining = new Map(columns.map((col) => [col.key, col]));
  const ordered = [];
  savedKeys.forEach((key) => {
    if (remaining.has(key)) {
      ordered.push(remaining.get(key));
      remaining.delete(key);
    }
  });
  remaining.forEach((col) => ordered.push(col));
  return ordered;
};

// NEW: enforce fixed columns stay leading and ordered
const enforceFixedColumns = (columns = [], sectionKey) => {
  const fixedColumns = getFixedColumnsForSection(sectionKey);
  const groupedKeys = getGroupedColumnsForSection(sectionKey);
  const map = new Map(columns.map(col => [col.key, col]));
  const fixed = fixedColumns.filter(key => map.has(key)).map(key => map.get(key));
  
  const grouped = groupedKeys.filter(key => map.has(key)).map(key => map.get(key));
  const groupedKeySet = new Set(grouped.map(c => c.key));

  const others = columns.filter(col => !fixedColumns.includes(col.key) && !groupedKeySet.has(col.key));
  
  if (grouped.length > 0) {
    let insertIndex = others.length;
    for (let i = 0; i < columns.length; i++) {
      if (groupedKeySet.has(columns[i].key)) {
        let beforeCount = 0;
        for (let j = 0; j < i; j++) {
          if (!fixedColumns.includes(columns[j].key) && !groupedKeySet.has(columns[j].key)) {
            beforeCount++;
          }
        }
        insertIndex = beforeCount;
        break;
      }
    }
    others.splice(insertIndex, 0, ...grouped);
  }

  return [...fixed, ...others];
};

const resolveColumnsWithPreference = (columns = [], sectionKey) => {
  if (!sectionKey) return enforceFixedColumns(columns);
  try {
    const savedOrder = getPreference(`columnOrder_${sectionKey}`);
    if (!savedOrder) return enforceFixedColumns(columns, sectionKey);
    return enforceFixedColumns(mergeColumnsWithSavedOrder(columns, savedOrder), sectionKey);
  } catch (error) {
    console.error("Error applying saved column order:", error);
    return enforceFixedColumns(columns, sectionKey);
  }
};

const BID_SIDE_COLUMN_KEYS = ["bidQuantity", "bidPrice", "bidYield"];
const OFFER_SIDE_COLUMN_KEYS = ["offerQuantity", "offerPrice", "offerYield"];

// NEW: helper to compute value text color (used on inner span to bypass parent !important)
const getValueColor = (columnKey, isBuy, isSell) => {
  if (isSell) return tokens.colors.sellFontColor;
  if (isBuy) return tokens.colors.buyFontColor;
  if (BID_SIDE_COLUMN_KEYS.includes(columnKey)) {
    return tokens.colors.buyFontColor;
  }
  if (OFFER_SIDE_COLUMN_KEYS.includes(columnKey)) {
    return tokens.colors.sellFontColor;
  }
  if (["totalGL", "realizedGL", "unrealizedGL"].includes(columnKey)) {
    return tokens.colors.buyFontColor;
  }
  return tokens.colors.fontColour;
};

// Add a helper to treat both "RFQ" and "TruMid RFQ" (and any other RFQ-like) as RFQ
const isRfqProtocol = (protocol) => {
  if (!protocol) return false;
  return String(protocol).toLowerCase().includes("rfq");
};

// NEW: treat MarketAxess RFQ rows as non-expandable
const isMarketAxessProtocol = (protocol) => {
  if (!protocol) return false;
  return String(protocol).toLowerCase().includes("marketaxess");
};

// NEW: identify protocols that follow enhanced RFQ rules
const isEnhancedProtocol = (protocol) => {
  if (!protocol) return false;
  const p = String(protocol).toLowerCase();
  return ENHANCED_RFQ_PROTOCOLS.some(ep => ep.toLowerCase() === p);
};

// NEW: rfqStatus-driven expansion policy for enhanced protocols
const normalizeRfqStatus = (s) => (s ? String(s).trim().toUpperCase() : "");
const canEnhancedRfqExpand = (rfqStatus, hasQuotes = true) => {
  const s = normalizeRfqStatus(rfqStatus);
  if (s === "MANUAL_TRADED") {
    return hasQuotes;
  }
  if (s === "SENT") return false;
  if (s === "REJECTED") return false;
  if (s === "OPEN") return true;
  if (s === "TRADED") return true;
  if (s === "DUE IN TIME OVER") return true;
  if (s === "EXPIRED") return true;
  if (s === "MANUAL_TAKE_OVER") return true;
  // Default safe: allow expansion
  return true;
};

// NEW: unified expansion gate used everywhere
const canExpandRfq = (rfqStatus, protocol, hasQuotes = true) => {
  if (isMarketAxessProtocol(protocol)) {
    return false;
  }
  return isEnhancedProtocol(protocol)
    ? canEnhancedRfqExpand(rfqStatus, hasQuotes)
    : true; // legacy RFQ rows always allowed to expand
};

// MA Inventory live market-data columns: these tick over the websocket feed, so
// a brief primary-colour flash on the changed cell signals the update to the user.
const MA_LIVE_FLASH_COLUMN_KEYS = new Set([
  "bidDealer",
  "bidYield",
  "bidQuantity",
  "bidPrice",
  "offerPrice",
  "offerQuantity",
  "offerYield",
  "offerDealer",
]);

// A cell value that renders as "---" (no live quote on this side). We must never
// flash these — see the guard in MAFlashValue below.
const isBlankFlashValue = (value) =>
  value === null || value === undefined || value === "";

// Flashes its cell background with the primary colour when either:
//  - `value` changes (a live tick on a row already on the page), or
//  - `entered` rises from false → true (the row just moved into the visible
//    page because of a live data change; the page decides when this is true).
// `rowKey` (the row's stable identity, e.g. cusip) guards against a false flash
// when the same component instance is reused for a different row after a
// re-sort: when the identity changes we re-baseline instead of flashing.
// Remounting via the incrementing `key` restarts the CSS animation each time.
// A blank/null value (renders as "---") NEVER flashes: neither a quote clearing
// to "---" (real → null) nor a row entering the page with no quote on this side
// may blink. Because `flashId` is only bumped for real values, a cell showing
// "---" keeps its previous `flashId`/key, so no animation is (re)started for it.
const MAFlashValue = React.memo(function MAFlashValue({ value, rowKey, entered = false, children }) {
  const prevValueRef = useRef(value);
  const prevRowKeyRef = useRef(rowKey);
  const prevEnteredRef = useRef(entered);
  const [flashId, setFlashId] = useState(0);

  useEffect(() => {
    if (prevRowKeyRef.current !== rowKey) {
      prevRowKeyRef.current = rowKey;
      prevValueRef.current = value;
      prevEnteredRef.current = entered;
      return;
    }
    const valueChanged = !Object.is(prevValueRef.current, value);
    const justEntered = entered && !prevEnteredRef.current;
    prevValueRef.current = value;
    prevEnteredRef.current = entered;
    // Never flash a blank/null cell ("---"), whether it just cleared or the row
    // entered the page with nothing on this side.
    if (isBlankFlashValue(value)) {
      return;
    }
    if (valueChanged || justEntered) {
      setFlashId((id) => id + 1);
    }
  }, [value, rowKey, entered]);

  return (
    <Box
      key={flashId}
      component="span"
      sx={{
        display: "inline-block",
        borderRadius: "3px",
        // Constant padding + negative margin so the flash has breathing room
        // without shifting the cell's layout when it triggers.
        padding: "0 4px",
        margin: "0 -4px",
        ...(flashId > 0
          ? {
              "@keyframes maCellFlash": {
                "0%": { backgroundColor: withAlpha(tokens.palette.primary, 0.45) },
                "100%": { backgroundColor: "transparent" },
              },
              animation: "maCellFlash 0.7s ease-out",
            }
          : {}),
      }}
    >
      {children}
    </Box>
  );
});

// Opens the same popup as the List ID / List Name cells. A pencil when that popup would let
// this user rename (buildViewSharedFields) or cancel (ListCancelButton) the list, else info.
// Both gates are mirrored from the popup on the list's rows, owner being the rows' entryBy.
const ListGroupViewIcon = ({ listGroup, handleViewJson }) => {
  const orders = listGroup.orders.map(({ order }) => order);
  const first = orders[0];
  const { loading, handleClick } = useOpenListView(first, handleViewJson);
  const items = orders.map((order) => ({ status: order.rfqStatus }));
  const ownsList = String(first?.entryBy ?? "") === String(localStorage.getItem("userId") ?? "");
  const protocol = String(first?.protocol || "").toLowerCase();
  const canCancel = ownsList && !!first?.account && !!(first?.tradingSessionSubId || first?.mktxListId)
    && (ENHANCED_RFQ_PROTOCOLS.some((name) => name?.toLowerCase() === protocol) || protocol.includes("marketaxess"))
    && hasCancellableListItem(items);
  const canRename = ownsList && items.some((item) => isListNameEditEligible(item.status));
  const Icon = canRename || canCancel ? BorderColor : InfoOutlined;
  return (
    <Icon
      titleAccess={canRename || canCancel ? "Edit list" : "View list"}
      sx={{ color: tokens.colors.fontColour, fontSize: "18px", cursor: "pointer", mr: 1 }}
      onClick={(e) => {
        e.stopPropagation();
        if (!loading) handleClick();
      }}
    />
  );
};

const BondTable = ({
  source = null,
  selectedColumns,
  filteredData,
  handleRowClick,
  handleViewJson,
  handleBuyClick,
  handleCusipClick,
  handleAllocateAccount, // NEW
  onOrderAdded,
  orderData,
  setOrderData,
  sectionKey = "default",
  onSort,
  sortConfig: externalSortConfig, // Add this prop
  decodeHighlightedRow,
  decodeHighlightActive = false,
  maInventoryData = null, // full MA Inventory data array for dealer expansion
  scrollResetSignal,
  flashRowCusips, // Set<cusip> of MA rows that just entered the visible page
  filteredColumnKeys, // Set<columnKey> of MA columns with an active filter
  groupListRfqs = false,
}) => {
  const tableContainerRef = useRef(null);
  // Store the raw column data separate from the rendered columns
  const [rawColumns, setRawColumns] = useState([]);
  // Use useMemo to derive columns with render functions
  const columns = useMemo(() => {
    console.log(`BondTable: Applying render functions to columns: source=${source}, sectionKey=${sectionKey}`);
    // Clean up sectionKey if it's in URL params format (e.g., extract "1-2" from "tab=1-2")
    const cleanedSectionKey = sectionKey && sectionKey.includes('=')
      ? sectionKey.split('=').pop()
      : sectionKey;

    if (cleanedSectionKey !== sectionKey) {
      console.log(`Using cleaned section key: ${cleanedSectionKey} (original: ${sectionKey})`);
    }

    return applyRenderFunctions(rawColumns, source, cleanedSectionKey || sectionKey);
  }, [rawColumns, source, sectionKey]);
  const auth = useAuth();
  // Replace internal sortConfig state with external one
  const sortConfig = externalSortConfig || { key: null, direction: "asc" };
  const [columnWidths, setColumnWidths] = useState(() =>
    getInitialColumnWidths(selectedColumns, sectionKey, source)
  );
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [orderEntryOpen, setOrderEntryOpen] = useState(false);
  const userRole = localStorage.getItem("role");
  const currentUserId = auth?.userId || localStorage.getItem("userId");
  const tableData = useMemo(
    () => (Array.isArray(filteredData) ? filteredData.filter((row) => row && typeof row === "object") : []),
    [filteredData]
  );

  const [expandedRows, setExpandedRows] = useState({});
  const [expandedLists, setExpandedLists] = useState({});
  const displayRows = useMemo(() => {
    if (!groupListRfqs) return tableData.map((order, rowIndex) => ({ order, rowIndex }));
    return groupListRfqOrders(tableData).flatMap((entry) => {
      if (!entry.listGroup) return [entry];
      return expandedLists[entry.listGroup.key]
        ? [entry, ...entry.listGroup.orders.map((child) => ({ ...child, isListChild: true }))]
        : [entry];
    });
  }, [tableData, groupListRfqs, expandedLists]);
  const protocolCellRefs = useRef({});
  // MA Inventory cusip expansion state
  const [maCusipExpandedRows, setMaCusipExpandedRows] = useState({});
  const cusipCellRefs = useRef({});
  const fixedColumns = useMemo(() => {
    const fixed = getFixedColumnsForSection(sectionKey);
    const grouped = getGroupedColumnsForSection(sectionKey);
    return [...fixed, ...grouped];
  }, [sectionKey]);
  const hasOrderQuotes = (orderId, fallbackRow = null) => {
    if (!orderId) return false;
    if (fallbackRow && Array.isArray(fallbackRow.orderQuotes) && fallbackRow.orderQuotes.length > 0) {
      return true;
    }
    if (!Array.isArray(orderData)) return false;
    const match = orderData.find(
      (order) => String(order.orderId) === String(orderId)
    );
    return Array.isArray(match?.orderQuotes) && match.orderQuotes.length > 0;
  };

  // Load saved column widths on initial render
  useEffect(() => {
    try {
      const savedWidths = getPreference(`columnWidths_${sectionKey}`);
      if (savedWidths) {
        const actionMinWidth = getActionMinWidthForSection(sectionKey, source);
        const parsedWidths = {
          ...getInitialColumnWidths(selectedColumns || [], sectionKey, source),
          ...savedWidths,
          viewInventory: VIEW_INVENTORY_WIDTH,
          protocol: PROTOCOL_WIDTH,
          actions: actionMinWidth,
          ...(source === "ma-inventory" ? { source: MA_SOURCE_WIDTH } : {}),
        };
        // removed action_account_pos hard-set here
        setColumnWidths(parsedWidths);
      } else {
        // No preference exists yet: initialize from column.width prop or use minimal defaults
        const defaults = { protocol: PROTOCOL_WIDTH, viewInventory: VIEW_INVENTORY_WIDTH };
        if (source === "ma-inventory") {
          defaults.source = MA_SOURCE_WIDTH;
        }

        // Initialize widths from rawColumns if they have width property
        rawColumns.forEach((col) => {
          if (col.width && col.key !== "protocol" && col.key !== "viewInventory") {
            defaults[col.key] = col.width;
          }
        });

        setColumnWidths((prev) => ({ ...defaults, ...prev }));
        setPreference(`columnWidths_${sectionKey}`, defaults);
      }
    } catch (e) {
      console.error("Error loading saved column widths:", e);
    }
  }, [sectionKey, rawColumns, selectedColumns, source]);

  // Add new effect to save column widths whenever they change
  useEffect(() => {
    // Only save if we have widths to save and after initial load
    if (Object.keys(columnWidths).length > 0) {
      try {
        const toSave = {
          ...columnWidths,
          protocol: PROTOCOL_WIDTH,
          viewInventory: VIEW_INVENTORY_WIDTH,
          actions: getActionMinWidthForSection(sectionKey, source),
          ...(source === "ma-inventory" ? { source: MA_SOURCE_WIDTH } : {}),
        };
        setPreference(`columnWidths_${sectionKey}`, toSave);
      } catch (e) {
        console.error("Error saving column widths:", e);
      }
    }
  }, [columnWidths, sectionKey, source]);

  // Add a state to track last expanded orderId to avoid repeated expansion attempts
  const [lastExpandedOrderId, setLastExpandedOrderId] = useState(null);
  // Add a state to track pending expansions
  const [pendingExpansion, setPendingExpansion] = useState(null);

  // Expand RFQ row if expandOrderId is present in URL and data is loaded
  useEffect(() => {
    // Only run if filteredData is loaded and not empty
    if (!tableData.length) return;

    const params = new URLSearchParams(window.location.search);
    const expandOrderId = params.get("expandOrderId");

    // Only attempt expansion if expandOrderId is present
    if (expandOrderId) {
      // Check if we need to expand this row
      const needsExpansion = expandOrderId !== lastExpandedOrderId || !expandedRows[expandOrderId];

      if (needsExpansion) {
        const rfqRow = tableData.find(
          (row) =>
            String(row.orderId) === String(expandOrderId) &&
            isRfqProtocol(row.protocol)
        );

        if (rfqRow) {
          // A linked order must be visible even when its protocol/status disallows quotes.
          const listKey = groupListRfqs && getListRfqGroupKey(rfqRow);
          if (listKey) setExpandedLists((prev) => prev[listKey] ? prev : { ...prev, [listKey]: true });
          const hasQuotes = hasOrderQuotes(expandOrderId);
          if (!canExpandRfq(rfqRow.rfqStatus, rfqRow.protocol, hasQuotes)) {
            console.log(`RFQ ${expandOrderId} cannot expand due to rfqStatus=${rfqRow.rfqStatus}`);
            return;
          }

          // Set expansion state
          setExpandedRows((prev) => ({
            ...prev,
            [expandOrderId]: true,
          }));
          setLastExpandedOrderId(expandOrderId);
          setPendingExpansion(expandOrderId);
        } else {
          console.log(`RFQ row with orderId ${expandOrderId} not found in data`);
        }
      }
    }
  }, [tableData, expandedRows, lastExpandedOrderId, orderData, groupListRfqs]);

  // Separate useEffect to clean up URL after expansion is confirmed
  useEffect(() => {
    if (pendingExpansion && expandedRows[pendingExpansion]) {
      // Wait a moment to ensure the expansion has been processed in the UI
      const timer = setTimeout(() => {
        const params = new URLSearchParams(window.location.search);
        params.delete("expandOrderId");

        const newUrl =
          window.location.pathname +
          (params.toString() ? `?${params.toString()}` : "");

        window.history.replaceState({}, "", newUrl);
        setPendingExpansion(null);
      }, 300); // Short delay to ensure expansion is visible

      return () => clearTimeout(timer);
    }
  }, [pendingExpansion, expandedRows]);

  // Reset expansion tracking when URL no longer has expandOrderId
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const expandOrderId = params.get("expandOrderId");

    if (!expandOrderId && lastExpandedOrderId && !pendingExpansion) {
      setLastExpandedOrderId(null);
    }
  }, [lastExpandedOrderId, pendingExpansion]);

  // Function to get or create a ref for each protocol cell
  const getProtocolCellRef = (rowData) => {
    // Use orderId as the unique identifier for this row
    const orderId = rowData.orderId;

    if (!orderId) {
      console.warn("Row is missing orderId:", rowData);
      return React.createRef(); // Return a new ref as fallback
    }

    if (!protocolCellRefs.current[orderId]) {
      protocolCellRefs.current[orderId] = React.createRef();
    }
    return protocolCellRefs.current[orderId];
  };

  // New function to toggle RFQ expansion
  const handleRfqExpand = (event, rowData) => {
    event.stopPropagation(); // Prevent row click event

    // Ensure the row has orderId
    if (!rowData.orderId) {
      console.warn("Cannot expand row without orderId", rowData);
      return;
    }

    const hasQuotes = hasOrderQuotes(rowData.orderId, rowData);
    if (!canExpandRfq(rowData.rfqStatus, rowData.protocol, hasQuotes)) {
      return;
    }

    setExpandedRows((prev) => ({
      ...prev,
      [rowData.orderId]: !prev[rowData.orderId],
    }));
  };

  // --- MA Inventory cusip expansion helpers ---
  const getCusipCellRef = (cusip) => {
    if (!cusip) return React.createRef();
    if (!cusipCellRefs.current[cusip]) {
      cusipCellRefs.current[cusip] = React.createRef();
    }
    return cusipCellRefs.current[cusip];
  };

  const handleMACusipExpand = (event, rowData) => {
    event.stopPropagation();
    const cusip = rowData.cusip;
    if (!cusip) return;
    setMaCusipExpandedRows((prev) => ({
      ...prev,
      [cusip]: !prev[cusip],
    }));
  };

  const getDealersForCusip = (cusip) => {
    if (!maInventoryData || !Array.isArray(maInventoryData)) return [];
    const row = maInventoryData.find((r) => r.cusip === cusip);
    return row?.dealers || [];
  };

  // Resizing refs
  const resizingColumnRef = useRef(null);
  const initialWidthRef = useRef(0);
  const initialXRef = useRef(0);

  const calculateMaxContentWidth = (columnKey) => {
    if (columnKey === "protocol") return PROTOCOL_WIDTH;
    if (columnKey === "viewInventory") return VIEW_INVENTORY_WIDTH;
    if (columnKey === "actions") return getActionMinWidthForSection(sectionKey, source);
    if (columnKey === "source" && source === "ma-inventory") return MA_SOURCE_WIDTH;
    // Check if we have a saved width for this column
    const savedWidth = columnWidths[columnKey];
    if (savedWidth) {
      return savedWidth;
    }

    const span = document.createElement("span");
    span.style.visibility = "hidden";
    span.style.position = "absolute";
    span.style.whiteSpace = "nowrap";
    document.body.appendChild(span);

    // Get header width first
    span.textContent =
      selectedColumns.find((col) => col.key === columnKey)?.name || "";
    let maxWidth = span.offsetWidth;

    tableData.forEach((row) => {
      const value = formatNumber(row[columnKey], columnKey, row);
      span.textContent = value?.toString() || "";
      maxWidth = Math.max(maxWidth, span.offsetWidth);
    });

    document.body.removeChild(span);

    // Use smaller padding for numeric columns vs text columns
    const isNumericColumn = [
      "qty",
      "quantity",
      "price",
      "minQty",
      "maxQty",
      "ytm",
      "ytw1",
    ].includes(columnKey);
    // const padding = isNumericColumn ? 27 : 32;
    const padding = 40;

    // Add appropriate padding to the content width
    const paddedWidth = maxWidth + padding;

    // For inventory source, use 85px as the maximum width
    if (source === "inventory") {
      return Math.min(paddedWidth, 85); // Cap at 85px for inventory source
    }

    // For non-inventory sources, keep the original 150px max width
    return Math.min(paddedWidth, 150); // 150 is imp for rfq branch view alignment of vertical line and start of table
  };

  // NOTE: `tableData` is intentionally NOT a dependency. Widths are only computed
  // for columns not already in the map, so re-running on data changes recomputes
  // nothing — but with live market data replacing the rows array up to 60×/sec it
  // forced an extra state update + render on every frame (React's "Maximum update
  // depth exceeded" warning under sustained ticking). Column additions still size
  // themselves from the rows current at that moment via the render closure.
  useEffect(() => {
    const orderedColumns = resolveColumnsWithPreference(selectedColumns || [], sectionKey);
    setRawColumns((prev) =>
      prev.length === orderedColumns.length && prev.every((col, i) => col === orderedColumns[i])
        ? prev
        : orderedColumns
    );
    setColumnWidths((prev) => {
      const newWidths = { ...prev };
      (selectedColumns || []).forEach((col) => {
        if (col.key === "protocol") {
          newWidths[col.key] = PROTOCOL_WIDTH;
        } else if (col.key === "viewInventory") {
          newWidths[col.key] = VIEW_INVENTORY_WIDTH;
        } else if (col.key === "actions") {
          newWidths[col.key] = getActionMinWidthForSection(sectionKey, source);
        } else if (col.key === "source" && source === "ma-inventory") {
          newWidths[col.key] = MA_SOURCE_WIDTH;
        } else if (col.width) {
          // Use explicit width if provided
          newWidths[col.key] = col.width;
        } else if (!(col.key in newWidths)) {
          newWidths[col.key] =
            source === "inventory"
              ? getDefaultWidthForColumn(col, source)
              : calculateMaxContentWidth(col.key);
        }
      });
      Object.keys(newWidths).forEach((key) => {
        if (!(selectedColumns || []).some((col) => col.key === key)) {
          delete newWidths[key];
        }
      });
      newWidths.protocol = PROTOCOL_WIDTH;
      newWidths.viewInventory = VIEW_INVENTORY_WIDTH;
      if (source === "ma-inventory") {
        newWidths.source = MA_SOURCE_WIDTH;
      }
      // Bail out with the previous reference when nothing changed so this state
      // update never schedules a redundant render.
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(newWidths);
      const unchanged =
        prevKeys.length === nextKeys.length
        && nextKeys.every((key) => prev[key] === newWidths[key]);
      return unchanged ? prev : newWidths;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, sectionKey, source]);

  // Keep legacy behavior for other pages; MA Inventory scroll reset is driven
  // by explicit user pagination changes via scrollResetSignal.
  useEffect(() => {
    if (source === "ma-inventory" || sectionKey === "ma-inventory") {
      return;
    }

    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [tableData, sectionKey, source]);

  useEffect(() => {
    if (!(source === "ma-inventory" || sectionKey === "ma-inventory")) {
      return;
    }
    if (scrollResetSignal === undefined || scrollResetSignal === null) {
      return;
    }

    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [scrollResetSignal, sectionKey, source]);

  // **Sorting Logic**
  // Modify handleSort to use external sort handler
  const handleSort = (columnKey) => {
    const newDirection =
      sortConfig?.key === columnKey && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";
    onSort(columnKey, newDirection);
  };

  // const sortedData = [...filteredData].sort((a, b) => {
  //   if (!sortConfig.key) return 0;

  //   if (sortConfig.key === "protocol") {
  //     const valA = a[sortConfig.key] || "CLOB";
  //     const valB = b[sortConfig.key] || "CLOB";

  //     if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
  //     if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
  //     return 0;
  //   }

  //   const valA = a[sortConfig.key];
  //   const valB = b[sortConfig.key];
  //   if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
  //   if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
  //   return 0;
  // });

  // **Drag-and-Drop Logic**
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const movedColumn = rawColumns[result.source.index];
    if (!movedColumn) return;
    if (fixedColumns.includes(movedColumn.key)) return;

    // Only block dropping before the actual fixed columns present in current set
    const rigidFixedKeys = getFixedColumnsForSection(sectionKey);
    const actualFixedCount = rawColumns.filter(col => rigidFixedKeys.includes(col.key)).length;
    if (result.destination.index < actualFixedCount) return;

    const reorderedColumns = [...rawColumns];
    const [moved] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, moved);

    const enforced = enforceFixedColumns(reorderedColumns, sectionKey);
    setRawColumns(enforced);
    if (sectionKey) {
      setPreference(`columnOrder_${sectionKey}`, serializeColumnKeys(enforced));
      // Update full column order while maintaining invisible columns
      const savedFullOrder = getPreference(`fullColumnOrder_${sectionKey}`);
      if (savedFullOrder) {
        try {
          const fullOrderedColumns = savedFullOrder;

          // Create a map of visible column positions
          const newPositions = {};
          reorderedColumns.forEach((col, idx) => {
            newPositions[col.key] = idx;
          });

          // Create sets for quick lookup
          const visibleKeys = new Set(reorderedColumns.map(col => col.key));
          const fullOrderKeys = new Set(fullOrderedColumns.map(col => col.key));

          // Reorder visible columns and keep invisible ones in their relative positions
          const newFullOrder = [];

          // First add all visible columns in their new order
          reorderedColumns.forEach(col => {
            newFullOrder.push(col);
          });

          // Then add all invisible columns in their original order
          fullOrderedColumns.forEach(col => {
            if (!visibleKeys.has(col.key)) {
              newFullOrder.push(col);
            }
          });

          // Save the updated full order
          setPreference(`fullColumnOrder_${sectionKey}`, newFullOrder);

          // Also update selected columns to match current order
          const savedSelectedColumns = getPreference(`selectedColumns_${sectionKey}`);
          if (savedSelectedColumns) {
            const selectedColumns = savedSelectedColumns;
            const reorderedSelected = reorderedColumns.filter(col =>
              selectedColumns.some(selected => selected.key === col.key)
            );
            setPreference(`selectedColumns_${sectionKey}`, reorderedSelected);
          }
        } catch (e) {
          console.error("Error updating full column order:", e);
        }
      }
    }
  };

  // Update useEffect for column initialization - now only sets raw columns
  useEffect(() => {
    console.log(`BondTable: Initializing columns with source=${source}, sectionKey=${sectionKey}`);
    const savedSelectedColumns = getPreference(`selectedColumns_${sectionKey}`);
    if (!savedSelectedColumns && selectedColumns) {
      setPreference(`selectedColumns_${sectionKey}`, selectedColumns);
      console.log(`Initialized selectedColumns_${sectionKey}`);
    }
    const resolved = resolveColumnsWithPreference(selectedColumns || [], sectionKey);
    if (resolved.length) {
      setRawColumns(resolved);
    }
    if (sectionKey && selectedColumns) {
      const savedFullOrder = getPreference(`fullColumnOrder_${sectionKey}`);
      if (!savedFullOrder) {
        setPreference(`fullColumnOrder_${sectionKey}`, selectedColumns);
        setPreference(`columnOrder_${sectionKey}`, serializeColumnKeys(selectedColumns));
      }
    }
  }, [selectedColumns, sectionKey, source]);

  // Update when selected columns change
  useEffect(() => {
    if (selectedColumns && selectedColumns.length > 0) {
      const resolved = resolveColumnsWithPreference(selectedColumns, sectionKey);
      setRawColumns(resolved);
    }
  }, [selectedColumns, sectionKey]);

  // Add initialization effect with version checking
  useEffect(() => {
    // Check column version and update if needed
    if (selectedColumns && sectionKey) {
      clearConfigIfOutdated(sectionKey, selectedColumns);
    }
    const savedFullOrder = getPreference(`fullColumnOrder_${sectionKey}`);
    const savedSelectedColumns = getPreference(`selectedColumns_${sectionKey}`);
    if (!savedFullOrder && !savedSelectedColumns && selectedColumns) {
      setPreference(`fullColumnOrder_${sectionKey}`, selectedColumns);
      setPreference(`selectedColumns_${sectionKey}`, selectedColumns);
      setPreference(`columnOrder_${sectionKey}`, serializeColumnKeys(selectedColumns));
      console.log(`BondTable: Initialized storage for section ${sectionKey}`);
      const columnsWithRenders = applyRenderFunctions(selectedColumns, source, sectionKey);
      setRawColumns(columnsWithRenders);
    }
  }, [sectionKey, selectedColumns, source]);

  // **Resizing Logic**
  const startResizing = (e, columnKey) => {
    e.stopPropagation();
    if (
      columnKey === "protocol" ||
      columnKey === "viewInventory" ||
      columnKey === "actions" ||
      columnKey === "source" ||
      columnKey === "action_account_pos"
    ) return;
    resizingColumnRef.current = columnKey;
    initialWidthRef.current = columnWidths[columnKey] || 150;
    initialXRef.current = e.clientX;
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
  };

  const handleResizing = (e) => {
    if (
      resizingColumnRef.current === "viewInventory" ||
      resizingColumnRef.current === "protocol" ||
      resizingColumnRef.current === "actions" ||
      resizingColumnRef.current === "source" ||
      resizingColumnRef.current === "action_account_pos"
    ) {
      return;
    }
    if (resizingColumnRef.current) {
      const delta = e.clientX - initialXRef.current;
      const newWidth = initialWidthRef.current + delta;

      let minWidth = 50;
      // NEW: enforce min for actions
      if (resizingColumnRef.current === "actions") {
        minWidth = getActionMinWidthForSection(sectionKey, source);
      } else if (resizingColumnRef.current === "viewInventory") {
        minWidth = VIEW_INVENTORY_WIDTH;
      }

      const maxWidth = resizingColumnRef.current === "protocol" ? PROTOCOL_WIDTH : 500;
      const clampedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));

      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumnRef.current]: clampedWidth,
      }));
    }
  };

  const stopResizing = () => {
    resizingColumnRef.current = null;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "";
  };

  // **Formatting Functions**
  const formatNumber = (value, columnKey, rowData) => {
    // NEW: helper to format time-only (HH:MM:SS) from ISO-like strings (handles microseconds, adds Z)
    function formatTimeOnly(dateStr) {
      try {
        if (dateStr === undefined || dateStr === null || dateStr === "") return "---";
        let valueStr = dateStr.toString();
        if (!valueStr.endsWith("Z")) valueStr += "Z";
        const date = new Date(valueStr);
        if (isNaN(date.getTime())) return "---";
        return date.toISOString().split("T")[1].substring(0, 8); // HH:MM:SS (UTC)
      } catch (e) {
        console.error("Error formatting time:", e, "for value:", dateStr);
        return "---";
      }
    }

    if (value === undefined || value === null || value === "") {
      // Special handling for protocol or venue: show N/A
      if (columnKey === "protocol" || columnKey === "venue") {
        return "N/A";
      }
      // Special handling for TTL column
      if (columnKey === "orderExpiryDtTime") {
        // Use rfqExpiryTime fallback and convert to local time
        const t = rowData?.rfqExpiryTime;
        if (t !== undefined && t !== null && t !== "") {
          const localDt = utcToLocal(t);
          return localDt ? localDt.format("DMMMYY HH:mm:ss") : "N/A";
        }
        return "N/A";
      }
      return "---";
    }

    // Format date in DDMonYY HH:MM format (e.g., 12Mar25 15:43)
    const formatDateToDDMonYY = (dateStr, showTime = true, includeSeconds = false) => {
      try {
        // Ensure timestamp is treated as UTC by appending 'Z' if not present
        let valueStr = dateStr.toString();
        if (!valueStr.endsWith("Z")) {
          valueStr += "Z";
        }

        const date = new Date(valueStr);
        if (isNaN(date.getTime())) return "---";

        const day = date.getUTCDate();
        const month = date.toLocaleString("en-US", { month: "short" });
        const year = date.getUTCFullYear().toString().slice(-2);

        // Add time in 24-hour format only if showTime is true
        if (showTime) {
          const hours = date.getUTCHours().toString().padStart(2, "0");
          const minutes = date.getUTCMinutes().toString().padStart(2, "0");
          const seconds = date.getUTCSeconds().toString().padStart(2, "0");
          return includeSeconds
            ? `${day}${month}${year} ${hours}:${minutes}:${seconds}`
            : `${day}${month}${year} ${hours}:${minutes}`;
        }

        // Return only the date portion
        return `${day}${month}${year}`;
      } catch (e) {
        console.error("Error formatting date:", e, "for value:", dateStr);
        return "---";
      }
    };

    // New helper to format maturity as MM/DD/YYYY
    const formatDateToMMDDYYYY = (dateStr) => {
      try {
        let valueStr = dateStr.toString();
        if (!valueStr.endsWith("Z")) {
          valueStr += "Z";
        }
        const date = new Date(valueStr);
        if (isNaN(date.getTime())) return "---";
        const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(date.getUTCDate()).padStart(2, "0");
        const yyyy = date.getUTCFullYear();
        return `${mm}/${dd}/${yyyy}`;
      } catch (e) {
        console.error("Error formatting date to MM/DD/YYYY:", e, "for value:", dateStr);
        return "---";
      }
    };

    // Special handling for orderExpiryDtTime - show in local time
    if (columnKey === "orderExpiryDtTime") {
      // Check for CLOB protocol in the row data
      if (rowData && (rowData.protocol === "CLOB" || !rowData.protocol)) {
        return "N/A";
      }
      const localDt = utcToLocal(value);
      return localDt ? localDt.format("DMMMYY HH:mm:ss") : "N/A";
    }

    // effective/expiry time: convert UTC → local time for display
    if (columnKey === "effectiveTime" || columnKey === "expiryTime") {
      const localDt = utcToLocal(value);
      return localDt ? localDt.format("DMMMYY HH:mm:ss") : "---";
    }

    // NEW: rfqExpiryTime - show time only (UTC)
    if (columnKey === "rfqExpiryTime") {
      if (rowData && (rowData.protocol === "CLOB" || !rowData.protocol)) {
        return "N/A";
      }
      return formatTimeOnly(value);
    }

    const yesNoColumns = [
      "insured",
      "altMinTax",
      "taxable",
      "callable",
      "perpetual",
      "zeroCoupon",
      // sec-master boolean fields
      "isCallable",
      "isPerpetual",
      "isZeroCoupon",
      "isInsured",
      "isExemptFromFedTax",
    ];

    if (yesNoColumns.includes(columnKey)) {
      // Accept numeric (1/0) and string forms ("Yes"/"No", "Y"/"N") from backend
      if (value === 1) return "Yes";
      if (value === 0) return "No";
      if (value === true) return "Yes";
      if (value === false) return "No";
      const s = typeof value === "string" ? value.trim().toLowerCase() : null;
      if (s === "yes" || s === "y" || s === "1" || s === "true") return "Yes";
      if (s === "no" || s === "n" || s === "0" || s === "false") return "No";
      return "---";
    }

    // Always show decimals for numeric columns
    if (NUMERIC_COLUMNS.has(columnKey)) {
      if (typeof value === "number" || !isNaN(Number(value))) {
        const num = Number(value);
        // Use 5 decimals for high-precision numeric columns, 0 for integer-like columns, otherwise 2
        const integerColumns = [
          "qty", "quantity", "minQty", "maxQty", "numberOfRungs", "totalMaturity", "amountPerRung", "executedQuantity",
          "bidQuantity", "offerQuantity",
          "quoteSubmissionDeadline", "ttl",
          "id", "totalRecords", "updatedRecords", "insertedRecords", "failedRecords",
          "histUpdatedRecords", "histInsertedRecords", "isActive" // Added for 0-decimal display
        ];
        const decimalPlaces = integerColumns.includes(columnKey)
          ? 0
          : HIGH_PRECISION_NUMERIC.has(columnKey)
            ? 5
            : 2;
        return num.toLocaleString("en-US", {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        });
      }
      return "---";
    }
    const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime", "maturityDate", "callEndDate", "callStartDate", "executionDateTime", "settlementDate", "orderExpiryDtTime", "rfqExpiryTime", "effectiveTime", "expiryTime", "processingDateTime", "startTime", "endTime", "issueDate", "firstCouponDate", "lastCouponDate"];
    if (dateColumns.includes(columnKey) && value) {
      // For maturity column, render as MM/DD/YYYY (no time)
      if (columnKey === "maturity" || columnKey === "maturityDate" || columnKey === "issueDate") {
        return formatDateToMMDDYYYY(value);
      }

      // For other date columns keep previous behavior (DDMonYY, optionally without time)
      if (columnKey === "nextCallDate" || columnKey === "callEndDate" || columnKey === "callStartDate" || columnKey === "firstCouponDate" || columnKey === "lastCouponDate") {
        return formatDateToDDMonYY(value, false);
      }

      // Order Date (dateTime): convert UTC → browser-local using shared util
      if (columnKey === "dateTime") {
        const localDt = utcToLocal(value);
        return localDt ? localDt.format("DMMMYY HH:mm") : "---";
      }

      return formatDateToDDMonYY(value);
    }

    if (columnKey === "coupon") {
      return value !== null && value !== undefined ? value : "---";
    }

    return value;
  };

  const isNumericOrDate = (value, columnKey) => {
    const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime", "maturityDate", "callEndDate", "callStartDate", "executionDateTime", "settlementDate", "orderExpiryDtTime", "rfqExpiryTime", "effectiveTime", "expiryTime", "processingDateTime", "startTime", "endTime", "issueDate", "firstCouponDate", "lastCouponDate"];
    return NUMERIC_COLUMNS.has(columnKey) || dateColumns.includes(columnKey);
  };

  const isBuySellColumn = (columnKey) => {
    return columnKey === "side" || columnKey === "action";
  };

  const isTypeLikeColumn = (columnKey) => {
    return (
      columnKey === "type" ||
      columnKey === "bondType" ||
      columnKey === "bondSubType"
    );
  };

  // Helper function to identify columns that should be bold
  const isBoldColumn = (columnKey) => {
    return ["securityMarketValue", "totalGL", "realizedGL", "unrealizedGL"].includes(columnKey);
  };

  // Helper function to identify columns that should be green
  const isGreenColumn = (columnKey) => {
    return ["totalGL", "realizedGL", "unrealizedGL"].includes(columnKey);
  };

  // **Context Menu Handlers**
  const handleContextMenu = (event, rowData) => {
    event.preventDefault();
    if (isStagingOrder(rowData) && userRole === "Trader") {
      setSelectedRowData(rowData);
      setContextMenu(
        contextMenu === null
          ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
          : null
      );
    }
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleSendToMarket = () => {
    handleCloseContextMenu();
    setOrderEntryOpen(true);
  };

  const rowHeight = "40px";
  const cellPadding = "4px 8px";

  const getColumnWidth = (column) => {
    if (column.key === "protocol") return PROTOCOL_WIDTH;
    if (column.key === "viewInventory") return VIEW_INVENTORY_WIDTH;
    if (column.key === "actions") return getActionMinWidthForSection(sectionKey, source);
    if (column.key === "source" && source === "ma-inventory") return MA_SOURCE_WIDTH;
    if (columnWidths[column.key] != null) return columnWidths[column.key];
    return getDefaultWidthForColumn(column, source);
  };

  // Get the first two column keys for positioning
  const firstTwoColumnKeys =
    columns.length >= 2 ? [columns[0].key, columns[1].key] : [];

  return (
    <Box
      sx={{
        px: "16px",
        py: "0px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <TableContainer
        component={Paper}
        ref={tableContainerRef}
        sx={{
          height: "100%",
          maxHeight: "100%",
          borderRadius: "0px",
          overflow: "auto",
          position: "relative",
          backgroundColor: tokens.colors.defaultGridRowColor,
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`,
            color: tokens.colors.fontColour,
          },
          "& .vertical-sticky-header": {
            position: "sticky",
            top: 0,
            backgroundColor: tokens.colors.defaultGridHeaderBackground,
            zIndex: 4,
          },
          "& .sticky-column": {
            position: "sticky !important",
            left: 0,
            zIndex: 3,
            backgroundColor: tokens.colors.defaultGridRowColor,
          },
          "& .sticky-header-and-column": {
            position: "sticky !important",
            top: 0,
            left: 0,
            zIndex: 5,
            backgroundColor: tokens.colors.defaultGridHeaderBackground,
          },
        }}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <Droppable
                droppableId="columns"
                direction="horizontal"
                type="column"
              >
                {(provided) => (
                  <TableRow
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {columns.map((column, index) => (
                      <Draggable
                        key={column.key}
                        draggableId={column.key}
                        index={index}
                        isDragDisabled={fixedColumns.includes(column.key)}
                      >
                        {(provided) => (
                          <TableCell
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              backgroundColor: tokens.colors.defaultGridHeaderBackground,
                              color: tokens.colors.headerFontColour,
                              padding: "4px 8px",
                              fontWeight: 700,
                              height: "40px",
                              userSelect: "none",
                              borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`,
                              width:
                                getColumnWidth(column),
                              minWidth: column.minWidth || undefined,
                              // ensure Actions header is centered
                              textAlign:
                                column.key === "viewInventory" || isActionColumnKey(column.key)
                                  ? "center"
                                  : undefined,
                              cursor: fixedColumns.includes(column.key) ? "default" : "grab",
                              position: "relative",
                            }}
                            className={
                              (source === "inventory" || source === "sec-master") && index === 0
                                ? "vertical-sticky-header sticky-header-and-column"
                                : "vertical-sticky-header"
                            }
                          >
                            {/* Drag handle wrapper */}
                            <div
                              {...(fixedColumns.includes(column.key) ? {} : provided.dragHandleProps)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  column.key === "viewInventory" || isActionColumnKey(column.key)
                                    ? "center"
                                    : isNumericOrDate(null, column.key)
                                      ? "flex-end"
                                      : isBuySellColumn(column.key)
                                        ? "center"
                                        : "flex-start",
                                height: "100%",
                                width: "100%",
                                paddingRight: "5px",
                                boxSizing: "border-box",
                                cursor: fixedColumns.includes(column.key) ? "default" : "grab",
                              }}
                            >
                              {source === "ma-inventory" && filteredColumnKeys?.has(column.key) ? (
                                <Box
                                  component="span"
                                  title="Filter active on this column"
                                  sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    backgroundColor: tokens.palette.primary,
                                    // Small gap so the dot sits snug to the left
                                    // of the heading as one group.
                                    mr: 0.5,
                                    flexShrink: 0,
                                  }}
                                />
                              ) : null}
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  marginRight: "4px",
                                }}
                                title={column.name}
                              >
                                {column.name}
                              </span>
                              {!UNSORTABLE_COLUMNS.includes(column.key) && column.sortable !== false && (
                                <span
                                  onClick={() => handleSort(column.key)}
                                  style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {sortConfig.key === column.key ? (
                                    sortConfig.direction === "asc" ? (
                                      <ArrowDropUp fontSize="small" />
                                    ) : (
                                      <ArrowDropDown fontSize="small" />
                                    )
                                  ) : (
                                    <UnfoldMore
                                      fontSize="small"
                                      sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
                                    />
                                  )}
                                </span>
                              )}
                            </div>
                            {/* Resize handle */}
                            <div
                              style={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: "5px",
                                backgroundColor: tokens.colors.bodyBorderColor50, // was rgba white
                                cursor:
                                  column.key === "protocol" ||
                                    column.key === "viewInventory" ||
                                    column.key === "actions" ||
                                    column.key === "source" ||
                                    column.key === "action_account_pos"
                                    ? "default"
                                    : "col-resize",
                                pointerEvents:
                                  column.key === "protocol" ||
                                    column.key === "viewInventory" ||
                                    column.key === "actions" ||
                                    column.key === "source" ||
                                    column.key === "action_account_pos"
                                    ? "none"
                                    : "auto",
                              }}
                              onMouseDown={(e) => startResizing(e, column.key)}
                            />
                          </TableCell>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableRow>
                )}
              </Droppable>
            </TableHead>
            <TableBody>
              {displayRows.map(({ order: rowData, rowIndex, listGroup, isListChild }, displayIndex) => {
                const previousRow = displayRows[displayIndex - 1];
                // Only distinguish a list group from a standalone order, keeping
                // adjacent lists and the group's expanded children together.
                const hasTypeBoundary = groupListRfqs && previousRow && !isListChild
                  && Boolean(listGroup) !== Boolean(previousRow.listGroup || previousRow.isListChild);
                const boundaryBorder = `1.5px solid ${tokens.colors.defaultGridBorderColor}`;
                if (listGroup) {
                  const expanded = Boolean(expandedLists[listGroup.key]);
                  const listLabel = listGroup.listName?.trim() || formatListId(listGroup.listId);
                  const accounts = [...new Set(listGroup.orders.map(({ order }) => order.account).filter(Boolean))];
                  const accountLabel = accounts.length > 1 ? 'Multiple' : accounts[0] || '---';
                  const userName = listGroup.orders[0]?.order.userName || '---';
                  return (
                    <TableRow key={`list-${listGroup.key}`}>
                      <TableCell colSpan={Math.max(columns.length, 1)} sx={{ padding: 0, borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`, borderTop: hasTypeBoundary ? boundaryBorder : undefined }}>
                        <ButtonBase
                          disableRipple
                          aria-label={`${expanded ? 'Collapse' : 'Expand'} list ${listLabel}`}
                          aria-expanded={expanded}
                          onClick={() => setExpandedLists((prev) => ({ ...prev, [listGroup.key]: !prev[listGroup.key] }))}
                          sx={{
                            width: '100%', justifyContent: 'flex-start', height: rowHeight,
                            padding: cellPadding, color: tokens.colors.fontColour,
                          }}
                        >
                          <PlayArrow sx={{ fontSize: 16, color: tokens.palette.primary, marginRight: '12px', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                          <Typography variant="body2" sx={{ color: tokens.colors.fontColour, fontWeight: 'bold', fontSize: '14px', marginRight: '8px' }}>{listLabel}</Typography>
                          <Typography variant="body2" sx={{ color: tokens.colors.fontColour80, fontWeight: 400, fontSize: '14px', marginRight: '8px' }}>(Total {listGroup.orders.length.toLocaleString()})</Typography>
                          <ListGroupViewIcon listGroup={listGroup} handleViewJson={handleViewJson} />
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            {[
                              { label: `Account: ${accountLabel}`, title: accounts.join(', ') },
                              { label: `Protocol: ${listGroup.protocol}`, title: listGroup.protocol },
                              ...(auth?.sharedBlotter ? [{ label: `User Name: ${userName}`, title: userName }] : []),
                            ].map(({ label, title }) => (
                              <Chip key={label} component="span" label={label} title={title} variant="outlined"
                                sx={{
                                  height: '22px', borderRadius: '8px', fontFamily: 'Lato', fontWeight: 700,
                                  fontSize: '0.75rem', lineHeight: '14px',
                                  backgroundColor: tokens.colors.defaultGridRowColor, color: tokens.colors.fontColour,
                                  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                                  '& .MuiChip-label': { padding: '0 6px' },
                                }}
                              />
                            ))}
                          </Box>
                        </ButtonBase>
                      </TableCell>
                    </TableRow>
                  );
                }
                const hasQuotes = hasOrderQuotes(rowData.orderId, rowData);
                const isDecodeHighlighted =
                  decodeHighlightActive &&
                  decodeHighlightedRow != null &&
                  decodeHighlightedRow === rowData;
                // console.log("has quotes", hasQuotes);
                // MA Inventory rows have no orderId — key them by cusip so React
                // preserves each row's component instance across live-tick re-sorts
                // (keeps the per-cell flash tracking anchored to the right bond).
                const rowReactKey =
                  rowData.orderId
                  || (source === "ma-inventory" && rowData.cusip
                    ? `ma-${rowData.cusip}`
                    : `row-${rowIndex}`);
                return (
                  <React.Fragment key={rowReactKey}>
                    <TableRow
                      onContextMenu={(e) => handleContextMenu(e, rowData)}
                      sx={{
                        cursor: "pointer",
                        height: "40px",
                        ...(hasTypeBoundary ? {
                          "& > .MuiTableCell-root": { borderTop: boundaryBorder },
                        } : {}),
                        // Match staging's expanded ladder rows, including cells whose
                        // sticky/column styles otherwise paint a white background.
                        ...(isListChild ? {
                          backgroundColor: tokens.colors.defaultGridHoverColor,
                          "& .MuiTableCell-root": {
                            backgroundColor: `${tokens.colors.defaultGridHoverColor} !important`,
                          },
                        } : {}),
                        ...(isDecodeHighlighted
                          ? {
                            "& .MuiTableCell-root": {
                              backgroundColor: `${withAlpha(tokens.palette.primary, 0.08)} !important`,
                            },
                          }
                          : {}),
                        "&:hover": isDecodeHighlighted
                          ? {
                            "& .MuiTableCell-root": {
                              backgroundColor: `${withAlpha(tokens.palette.primary, 0.12)} !important`,
                            },
                          }
                          : { backgroundColor: tokens.colors.defaultGridHoverColor }, // was rgba black
                      }}
                    >
                      {columns.map((column, colIndex) => {
                        const isNumOrDate = isNumericOrDate(
                          rowData[column.key],
                          column.key
                        );
                        const isBuy =
                          rowData[column.key] === "BUY" ||
                          rowData[column.key] === "Buy";
                        const isSell =
                          rowData[column.key] === "SELL" ||
                          rowData[column.key] === "Sell";
                        const formattedValue = formatNumber(
                          rowData[column.key],
                          column.key,
                          rowData
                        );

                        const expandable = isRfqProtocol(rowData[column.key]) && canExpandRfq(rowData.rfqStatus, rowData.protocol, hasQuotes);

                        // NEW: Check for allocate account condition
                        const isAccountColumn = column.key === "account";
                        const isMissingAccount = rowData[column.key] === undefined || rowData[column.key] === null || rowData[column.key] === "";
                        const isEntryByUser = String(rowData.entryBy || "") === String(currentUserId || "");
                        const showAllocateAccount = isAccountColumn && isMissingAccount && isEntryByUser && currentUserId;

                        if (
                          column.key === "protocol" &&
                          rowData[column.key] === "RFQ"
                        ) {
                          const orderId = rowData.orderId;
                          const isExpanded = expandedRows[orderId];
                          const protocolCellRef = getProtocolCellRef(rowData);

                          return (
                            <TableCell
                              key={column.key}
                              ref={protocolCellRef}
                              onClick={expandable ? (e) => handleRfqExpand(e, rowData) : undefined}
                              sx={{
                                height: "40px",
                                padding: "4px 8px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                backgroundColor: tokens.colors.defaultGridRowColor,
                                color: tokens.colors.fontColour,
                                width: PROTOCOL_WIDTH,
                                cursor: expandable ? "pointer" : "default",
                                "&:hover": expandable ? { backgroundColor: tokens.colors.defaultGridHoverColor } : {},
                              }}
                              className={source === "inventory" && colIndex === 0 ? "sticky-column" : ""}
                            >
                              <Box
                                sx={{
                                  pr: 1,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: "100%",
                                  position: "relative",
                                  zIndex: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textDecoration: expandable ? "underline" : "none",
                                    color: tokens.colors.fontColour,
                                  }}
                                >
                                  RFQ
                                </Typography>
                                {expandable && (
                                  <PlayArrow
                                    sx={{
                                      fontSize: "14px",
                                      color: tokens.colors.fontColour,
                                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          );
                        } else if (column.key === "protocol") {
                          const protocolValue = rowData[column.key];
                          const displayText = formatNumber(protocolValue, "protocol", rowData);
                          const orderId = rowData.orderId;
                          const isRfq = isRfqProtocol(protocolValue);
                          const expandable = isRfq && canExpandRfq(rowData.rfqStatus, protocolValue, hasQuotes);
                          const isExpanded = expandedRows[orderId];
                          const protocolCellRef = isRfq ? getProtocolCellRef(rowData) : null;

                          return (
                            <TableCell
                              key={column.key}
                              ref={protocolCellRef}
                              onClick={expandable ? (e) => handleRfqExpand(e, rowData) : undefined}
                              sx={{
                                height: rowHeight,
                                padding: cellPadding,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                backgroundColor: tokens.colors.defaultGridRowColor,
                                color: tokens.colors.fontColour,
                                width: PROTOCOL_WIDTH,
                                cursor: expandable ? "pointer" : "default",
                                "&:hover": expandable ? { backgroundColor: tokens.colors.defaultGridHoverColor } : {},
                              }}
                              className={
                                source === "inventory" && colIndex === 0
                                  ? "sticky-column"
                                  : ""
                              }
                            >
                              {isRfq ? (
                                <Box
                                  sx={{
                                    pr: 1,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                    position: "relative",
                                    zIndex: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      textDecoration: expandable ? "underline" : "none",
                                      color: tokens.colors.fontColour,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={displayText}
                                  >
                                    {displayText}
                                  </Typography>
                                  {expandable && (
                                    <PlayArrow
                                      sx={{
                                        fontSize: "14px",
                                        color: tokens.colors.fontColour,
                                        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                      }}
                                    />
                                  )}
                                </Box>
                              ) : (
                                displayText
                              )}
                            </TableCell>
                          );
                        }

                        // MA Inventory — cusip column rendered as expandable
                        if (column.key === "cusip" && source === "ma-inventory") {
                          const cusipVal = rowData.cusip;
                          const isExpanded = maCusipExpandedRows[cusipVal];
                          const cusipCellRef = getCusipCellRef(cusipVal);
                          const hasDealers = getDealersForCusip(cusipVal).length > 0;

                          return (
                            <TableCell
                              key={column.key}
                              ref={cusipCellRef}
                              onClick={hasDealers ? (e) => handleMACusipExpand(e, rowData) : undefined}
                              sx={{
                                height: rowHeight,
                                padding: cellPadding,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                backgroundColor: tokens.colors.defaultGridRowColor,
                                color: tokens.colors.fontColour,
                                width: getColumnWidth(column),
                                cursor: hasDealers ? "pointer" : "default",
                                "&:hover": hasDealers ? { backgroundColor: tokens.colors.defaultGridHoverColor } : {},
                              }}
                            >
                              <Box
                                sx={{
                                  pr: 1,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: "100%",
                                  position: "relative",
                                  zIndex: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textDecoration: hasDealers ? "underline" : "none",
                                    color: tokens.colors.fontColour,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={cusipVal}
                                >
                                  {cusipVal}
                                </Typography>
                                {hasDealers && (
                                  <PlayArrow
                                    sx={{
                                      fontSize: "14px",
                                      color: tokens.colors.fontColour,
                                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          );
                        }

                        if (column.key === "viewInventory") {
                          return (
                            <TableCell
                              key={column.key}
                              sx={{
                                height: "40px",
                                padding: "4px 8px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                backgroundColor: tokens.colors.defaultGridRowColor,
                                color: tokens.colors.fontColour,
                                width: VIEW_INVENTORY_WIDTH,
                                position: "relative",
                                isolation: "isolate",
                                textAlign: "center",
                              }}
                            >
                              {column.render ? (
                                column.render(
                                  rowData[column.key],
                                  rowData,
                                  handleViewJson
                                )
                              ) : (
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "100%",
                                  }}
                                >
                                  ---
                                </span>
                              )}
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell
                            key={column.key}
                            sx={{
                              height: "40px",
                              padding: "4px 8px",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              // CENTER '---' for actions column
                              textAlign:
                                isActionColumnKey(column.key)
                                  ? "center"
                                  : isBuySellColumn(column.key)
                                    ? "center"
                                    : isTypeLikeColumn(column.key)
                                      ? "left"
                                      : isNumOrDate
                                        ? "right"
                                        : "left",
                              backgroundColor: tokens.colors.defaultGridRowColor,
                              // Keep cell color default; per-value color is applied on the child span
                              color: tokens.colors.fontColour,
                              fontWeight: isSell || isBuy
                                ? 700
                                : isBoldColumn(column.key)
                                  ? 700
                                  : 400,
                              width: column.key === "protocol"
                                ? PROTOCOL_WIDTH
                                : column.key === "actions"
                                  ? getActionMinWidthForSection(sectionKey, source)
                                  : getColumnWidth(column),
                              minWidth: column.minWidth || undefined,
                            }}
                            className={(source === "inventory" || source === "sec-master") && colIndex === 0 ? "sticky-column" : ""}
                            onClick={(e) => {
                              if (column.key === "cusip") {
                                e.stopPropagation();
                                handleCusipClick(rowIndex, rowData);
                              }
                            }}
                          >
                            {column.render ? (
                              column.render(rowData[column.key], rowData, handleViewJson)
                            ) : (
                              <>
                                {(isBuy || isSell) && source === "inventory" && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: isSell
                                        ? withAlpha(tokens.colors.sellBgColor, 0.1)
                                        : withAlpha(tokens.colors.buyBgColor, 0.1),
                                      zIndex: 1,
                                      isolation: "isolate",
                                      mixBlendMode: "normal",
                                    }}
                                  />
                                )}
                                <span
                                  style={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    // CENTER inner text when actions shows '---'
                                    textAlign:
                                      isActionColumnKey(column.key)
                                        ? "center"
                                        : isBuySellColumn(column.key)
                                          ? "center"
                                          : isTypeLikeColumn(column.key)
                                            ? "left"
                                            : isNumOrDate
                                              ? "right"
                                              : "left",
                                    lineHeight: "40px",
                                    textDecoration:
                                      showAllocateAccount ? "underline" :
                                        ((isBuy || isSell) && source === "inventory") ||
                                          column.key === "cusip" ||
                                          (column.key === "identifier" && source === "sec-master")
                                          ? "underline"
                                          : "none",
                                    position: "relative",
                                    zIndex: 2,
                                    // IMPORTANT: apply value color on child span to bypass parent !important
                                    color:
                                      showAllocateAccount
                                        ? tokens.palette.primary
                                        : (column.key === "identifier" && source === "sec-master")
                                          ? tokens.palette.primary
                                          : getValueColor(column.key, isBuy, isSell),
                                    fontWeight: (isSell || isBuy || isBoldColumn(column.key)) ? 700 : 400,
                                    cursor: showAllocateAccount ? "pointer" : "inherit" // NEW
                                  }}
                                  title={showAllocateAccount ? "Allocate Account" : formattedValue} // NEW
                                  onClick={(e) => {
                                    if (showAllocateAccount) { // NEW
                                      e.stopPropagation();
                                      if (handleAllocateAccount) handleAllocateAccount(rowData); // NEW
                                      return;
                                    }
                                    if (column.key === "cusip") {
                                      handleCusipClick(rowIndex, rowData);
                                    } else if (isBuy || isSell) {
                                      handleBuyClick(rowIndex, rowData);
                                    } else if (source === "sec-master") {
                                      if (handleCusipClick) handleCusipClick(rowIndex, rowData);
                                    } else {
                                      if (handleRowClick) handleRowClick(rowIndex, rowData);
                                    }
                                  }}
                                >
                                  {source === "ma-inventory" && MA_LIVE_FLASH_COLUMN_KEYS.has(column.key) ? (
                                    <MAFlashValue
                                      value={rowData[column.key]}
                                      rowKey={rowData.cusip}
                                      entered={Boolean(flashRowCusips?.has(rowData.cusip))}
                                    >
                                      {formattedValue}
                                    </MAFlashValue>
                                  ) : (
                                    showAllocateAccount ? "Allocate Account" : formattedValue
                                  )}
                                </span>
                              </>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {rowData.orderId &&
                      expandedRows[rowData.orderId] !== undefined &&
                      isRfqProtocol(rowData.protocol) &&
                      canExpandRfq(rowData.rfqStatus, rowData.protocol, hasQuotes) && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length}
                            sx={{
                              padding: 0,
                              borderBottom: "none",
                              backgroundColor: tokens.colors.defaultGridRowColor,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                overflow: "hidden",
                                transition:
                                  "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
                                maxHeight: expandedRows[rowData.orderId]
                                  ? "1000px"
                                  : "0px",
                                opacity: expandedRows[rowData.orderId] ? 1 : 0,
                                backgroundColor: tokens.colors.defaultGridRowColor,
                              }}
                            >
                              {expandedRows[rowData.orderId] && (
                                <RfqBranchTable
                                  parentRowData={rowData}
                                  parentCellRef={
                                    protocolCellRefs.current[rowData.orderId]
                                  }
                                  columnWidths={columnWidths}
                                  firstTwoColumnKeys={firstTwoColumnKeys}
                                  colsBondTable={columns}
                                  orderData={orderData}
                                  setOrderData={setOrderData}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                  
                    {/* MA Inventory cusip expansion row */}
                    {source === "ma-inventory" &&
                      rowData.cusip &&
                      maCusipExpandedRows[rowData.cusip] !== undefined && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length}
                            sx={{
                              padding: 0,
                              borderBottom: "none",
                              backgroundColor: tokens.colors.defaultGridRowColor,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                overflow: "hidden",
                                transition:
                                  "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
                                maxHeight: maCusipExpandedRows[rowData.cusip]
                                  ? "1000px"
                                  : "0px",
                                opacity: maCusipExpandedRows[rowData.cusip] ? 1 : 0,
                                backgroundColor: tokens.colors.defaultGridRowColor,
                              }}
                            >
                              {maCusipExpandedRows[rowData.cusip] && (
                                <MABranchTable
                                  parentRowData={rowData}
                                  parentCellRef={cusipCellRefs.current[rowData.cusip]}
                                  columnWidths={columnWidths}
                                  colsBondTable={columns}
                                  dealers={getDealersForCusip(rowData.cusip)}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}

                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </DragDropContext>
      </TableContainer>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleSendToMarket}>Send to market</MenuItem>
      </Menu>

      {orderEntryOpen && selectedRowData && (
        <OrderEntry
          open={orderEntryOpen}
          onClose={() => setOrderEntryOpen(false)}
          source="sendToMarket"
          initialData={selectedRowData}
          readOnly={false}
          token={localStorage.getItem("token")}
          onOrderAdded={onOrderAdded}
        />
      )}
    </Box>
  );
};

export default BondTable;

